// Response type for GET /incentive_programs/config/current.
// Shape defined by prime-app's program-config.md:
//   https://github.com/Clearcover/prime-app/blob/master/docs/26for26/program-config.md
// Outcomes are nested under config.tracks.TRACK1.phases[] — each phase carries
// its own attempt_type and gameplay outcomes. Fields are snake_case.

export type AttemptType =
  | 'SPIN'
  | 'SCRATCH_OFF'
  | 'RAFFLE_ENTRY'
  | 'TIER_EVAL'
  | 'TOP_PRODUCER_EVAL'
  | 'GRAND_PRIZE_EVAL';

/** One outcome row from the program config. Matches prime's EffectiveConfig.Outcome shape (snake_case). */
export type RawOutcomeConfig = {
  code: string;
  weight: number;
  prize_amount: number | null;
  is_win: boolean;
  max_wins?: number;
  downgrade_to?: string;
  review?: boolean;
  grants_entry_in_phase?: string;
};

export type RawPhaseConfig = {
  code: string;
  attempt_type: AttemptType;
  display_version: string;
  outcomes?: RawOutcomeConfig[];
  drawing?: {
    review_scope: string;
    prizes: Array<{
      code: string;
      win_rate: number;
      prize_amount: number | null;
      max_wins?: number;
      downgrade_to?: string;
      review?: boolean;
      grants_entry_in_phase?: string;
    }>;
  };
};

export type RawProgramConfig = {
  program: {
    code: string;
    entry_threshold: number;
  };
  tracks: {
    TRACK1: { type: 'PHASED'; phases: RawPhaseConfig[] };
    TRACK2?: Record<string, unknown>;
    TRACK3?: Record<string, unknown>;
  };
};

export type GetRewardsConfigResponse = {
  programCode: string;
  phase: {
    number: number;
    code: string;
    name: string;
    start: string;
    end: string;
    attemptType: AttemptType;
  };
  track: string;
  config: RawProgramConfig;
};

/** Internal shape the wheel components consume. Built by `outcomesForPhase`. */
export type OutcomeConfig = {
  /** Outcome code from the program config (e.g. LARGE/MEDIUM/SMALL/CONSOLATION). */
  code: string;
  /** Dollar amount (whole dollars) or null for non-monetary outcomes. */
  prizeAmount: number | null;
  /** Weight used by the backend resolver (relative ratio, not %). */
  weight: number;
  isWin: boolean;
  display: {
    label: string;
    /** Hex color for the wheel segment background. */
    color: string;
    /** Hex color for text/icon rendered on top of the segment. */
    textColor: string;
    /** Copy shown in the outcome modal after the wheel stops. */
    description: string;
    /** Heading shown in the outcome modal. Derived locally for non-win outcomes. */
    modalHeading?: string;
  };
};

type DisplayDefaults = {
  color: string;
  textColor: string;
  description: (amount: number | null) => string;
  modalHeading?: string;
  labelOverride?: string;
};

// Display metadata the backend config does not carry. Keyed by outcome code so
// the FE renders consistently regardless of prize_amount changes in config.
const DISPLAY_DEFAULTS_BY_CODE: Record<string, DisplayDefaults> = {
  SUPER: {
    color: '#1a237e',
    textColor: '#ffffff',
    description: amount =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
  },
  LARGE: {
    color: '#1a237e',
    textColor: '#ffffff',
    description: amount =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
  },
  MEDIUM: {
    color: '#3949ab',
    textColor: '#ffffff',
    description: amount =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
  },
  SMALL: {
    color: '#3b52d4',
    textColor: '#ffffff',
    description: amount =>
      `$${amount} is on its way to your Awardco account within 5 business days.`,
  },
  CONSOLATION: {
    color: '#4f6ee8',
    textColor: '#ffffff',
    description: () =>
      'You earned a bonus entry for the next Act. Use it when the next Act opens — or play your remaining entries now.',
    modalHeading: '+1 Entry',
    labelOverride: '+1',
  },
  NONE: {
    color: '#6366f1',
    textColor: '#ffffff',
    description: () =>
      'Keep binding policies to earn more entries and try again!',
    modalHeading: 'No prize this spin',
    labelOverride: 'No prize',
  },
};

const FALLBACK_DISPLAY: DisplayDefaults = {
  color: '#6366f1',
  textColor: '#ffffff',
  description: () => '',
};

function deriveDisplay(raw: RawOutcomeConfig): OutcomeConfig['display'] {
  const defaults = DISPLAY_DEFAULTS_BY_CODE[raw.code] ?? FALLBACK_DISPLAY;
  const label =
    defaults.labelOverride ??
    (raw.prize_amount != null ? `$${raw.prize_amount}` : raw.code);
  return {
    label,
    color: defaults.color,
    textColor: defaults.textColor,
    description: defaults.description(raw.prize_amount),
    modalHeading: defaults.modalHeading,
  };
}

/**
 * Extracts the gameplay outcomes for the currently active TRACK1 phase and
 * transforms them into the internal OutcomeConfig shape the wheel consumes.
 * Returns an empty array when the matching phase is missing or carries no
 * outcomes (e.g. RAFFLE_ENTRY phases use `drawing.prizes` instead).
 */
export function outcomesForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): OutcomeConfig[] {
  const phase = config.config.tracks.TRACK1.phases.find(
    p => p.code === phaseCode
  );
  if (!phase?.outcomes) return [];
  return phase.outcomes.map(raw => ({
    code: raw.code,
    prizeAmount: raw.prize_amount ?? null,
    weight: raw.weight,
    isWin: raw.is_win,
    display: deriveDisplay(raw),
  }));
}

/** Max prize_amount among winning outcomes in the given phase, or null if none. */
export function maxWinPrizeForPhase(
  config: GetRewardsConfigResponse,
  phaseCode: string
): number | null {
  const amounts = outcomesForPhase(config, phaseCode)
    .filter(o => o.isWin && o.prizeAmount != null)
    .map(o => o.prizeAmount as number);
  return amounts.length ? Math.max(...amounts) : null;
}

/** How many qualifying binds it takes to earn one entry. */
export function entryThresholdFromConfig(
  config: GetRewardsConfigResponse
): number {
  return config.config.program.entry_threshold;
}
